import { api } from './api';

// 消息类型定义
export interface Message {
  id: string;
  user_id: number;
  title: string;
  content: string;
  type: 'system' | 'service' | 'comment' | 'like' | 'follow';
  is_read: boolean;
  sender?: string;
  avatar?: string;
  related_item?: string;
  createdAt: string;
  updatedAt: string;
}

// 消息列表响应类型
export interface MessagesResponse {
  messages: Message[];
  total: number;
  pages: number;
  current_page: number;
  per_page: number;
  has_next: boolean;
  has_prev: boolean;
}

// 获取消息列表参数
export interface GetMessagesParams {
  page?: number;
  per_page?: number;
  type?: 'system' | 'service' | 'comment' | 'like' | 'follow';
  unread_only?: boolean;
}

// 获取用户消息列表
export async function getUserMessages(params?: GetMessagesParams): Promise<MessagesResponse> {
  const query = new URLSearchParams();
  
  if (params?.page) query.set('page', String(params.page));
  if (params?.per_page) query.set('per_page', String(params.per_page));
  if (params?.type) query.set('type', params.type);
  if (params?.unread_only) query.set('unread_only', 'true');
  
  const url = `/messages${query.toString() ? `?${query.toString()}` : ''}`;
  return api.get<MessagesResponse>(url);
}

// 获取未读消息数量
export async function getUnreadMessagesCount(): Promise<{ unread_count: number }> {
  // 检查是否有有效的 token，如果没有则直接返回 0，避免发送无效请求
  const token = typeof window !== 'undefined' ? localStorage.getItem('userToken') : null;
  if (!token || token.trim().length === 0) {
    console.log('未找到有效token，跳过获取未读消息数量');
    return { unread_count: 0 };
  }
  
  try {
    return await api.get<{ unread_count: number }>('/messages/unread-count');
  } catch (error: any) {
    // 静默处理401错误（token过期或无效是正常情况）
    if (error.message?.includes('登录已过期') || error.message?.includes('未授权')) {
      console.log('Token已过期，返回未读消息数量为0');
      return { unread_count: 0 };
    }
    // 其他错误继续抛出
    throw error;
  }
}

// 标记单个消息为已读
export async function markMessageAsRead(messageId: string): Promise<{ message: string; data: Message }> {
  return api.put<{ message: string; data: Message }>(`/messages/${messageId}/read`);
}

// 标记所有消息为已读
export async function markAllMessagesAsRead(): Promise<{ message: string }> {
  return api.put<{ message: string }>('/messages/mark-all-read');
}

// 批量标记消息为已读
export async function markBatchMessagesAsRead(messageIds: string[]): Promise<{ message: string }> {
  return api.put<{ message: string }>('/messages/batch-read', {
    message_ids: messageIds
  });
}

// 删除消息
export async function deleteMessage(messageId: string): Promise<{ message: string }> {
  return api.delete<{ message: string }>(`/messages/${messageId}`);
}

// 创建消息（管理员功能）
export interface CreateMessagePayload {
  user_id: number;
  title: string;
  content: string;
  type?: 'system' | 'service' | 'comment' | 'like' | 'follow';
  sender?: string;
  avatar?: string;
  related_item?: string;
}

export async function createMessage(payload: CreateMessagePayload): Promise<{ message: string; data: Message }> {
  return api.post<{ message: string; data: Message }>('/messages', payload);
}
